import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useEntityDoc } from '../data/entities';
import BreathingGuide from './BreathingGuide';
import PlanGate from './PlanGate';
import {
  formatDuration,
  loadPracticeSequence,
  plannedDurationSeconds,
  practicedSeconds,
  recordPracticeCompletion,
  saveInProgressPractice,
} from './practiceSession';
import { resolvePoseRef, fallbackPoseImage } from './poseImages';
import { stepPoseLabel } from './sequenceModel';
import { useUserProfile } from './useUserProfile';
import { canManageLessons, usesTeacherVoice } from './planTypes';

function useHoldTimer({ holdSeconds, paused, onComplete }) {
  const [remaining, setRemaining] = useState(holdSeconds);
  const endAtRef = useRef(null);
  const remainingRef = useRef(holdSeconds);
  const onCompleteRef = useRef(onComplete);

  useEffect(() => {
    onCompleteRef.current = onComplete;
  }, [onComplete]);

  useEffect(() => {
    remainingRef.current = holdSeconds;
    setRemaining(holdSeconds);
    endAtRef.current = paused ? null : Date.now() + holdSeconds * 1000;
  }, [holdSeconds]); // reset when step changes

  useEffect(() => {
    if (paused) {
      endAtRef.current = null;
      return undefined;
    }
    endAtRef.current = Date.now() + remainingRef.current * 1000;
    const tick = () => {
      const endAt = endAtRef.current;
      if (!endAt) return;
      const left = Math.max(0, (endAt - Date.now()) / 1000);
      remainingRef.current = left;
      setRemaining(left);
      if (left <= 0) {
        endAtRef.current = null;
        onCompleteRef.current?.();
      }
    };
    tick();
    const id = window.setInterval(tick, 200);
    return () => window.clearInterval(id);
  }, [paused, holdSeconds]);

  return remaining;
}

function PoseStill({ poseKey }) {
  const img = resolvePoseRef(poseKey) || fallbackPoseImage(poseKey || 'Pose');
  const [src, setSrc] = useState(img.url);
  useEffect(() => {
    setSrc(img.url);
  }, [img.url]);
  return (
    <figure className="yoga-player__pose">
      <img
        src={src}
        alt={img.alt || 'Pose'}
        onError={() => setSrc(fallbackPoseImage(img.alt || 'Pose').url)}
      />
    </figure>
  );
}

function PlayerInner() {
  const { lessonId } = useParams();
  const [search] = useSearchParams();
  const debugScale = Number(search.get('holdScale')) || 1;
  const resumeStep = Math.max(0, Math.floor(Number(search.get('step')) || 0));
  const navigate = useNavigate();
  const { uid, profile, planType } = useUserProfile();
  const { doc: lesson, error } = useEntityDoc('yogaLesson', {}, lessonId);
  const showTips = usesTeacherVoice(planType) || canManageLessons(planType);

  const sequence = useMemo(() => {
    if (!lesson) return [];
    const seq = loadPracticeSequence(lesson, planType);
    if (debugScale !== 1 && debugScale > 0) {
      return seq.map((s) => ({
        ...s,
        holdSeconds: Math.max(5, Math.round(s.holdSeconds * debugScale)),
      }));
    }
    return seq;
  }, [lesson, planType, debugScale]);

  const [index, setIndex] = useState(0);
  const [paused, setPaused] = useState(false);
  const [done, setDone] = useState(false);
  const [statsPatch, setStatsPatch] = useState(null);
  const advanceLock = useRef(false);
  const indexRef = useRef(0);
  const doneRef = useRef(false);

  useEffect(() => {
    indexRef.current = index;
  }, [index]);

  useEffect(() => {
    doneRef.current = done;
  }, [done]);

  useEffect(() => {
    const start = sequence.length
      ? Math.min(resumeStep, Math.max(0, sequence.length - 1))
      : 0;
    setIndex(start);
    setPaused(false);
    setDone(false);
    setStatsPatch(null);
  }, [lessonId, sequence.length, resumeStep]);

  const step = sequence[index] || null;
  const holdSeconds = step?.holdSeconds || 30;

  const finish = useCallback(async () => {
    setDone(true);
    if (uid && profile && lesson) {
      try {
        const practiced = practicedSeconds(sequence, sequence.length, 0);
        const patch = await recordPracticeCompletion(uid, profile, {
          lessonId: lesson.id,
          title: lesson.title,
          practicedSeconds: practiced,
          style: lesson.style,
          skillLevel: lesson.skillLevel,
        });
        setStatsPatch(patch);
      } catch {
        /* non-fatal */
      }
    }
  }, [uid, profile, lesson, sequence]);

  const exitPractice = useCallback(async () => {
    if (uid && lesson && !doneRef.current && sequence.length) {
      const stepIndex = indexRef.current;
      // Only bookmark if they actually started into the class.
      if (stepIndex > 0) {
        try {
          await saveInProgressPractice(uid, {
            lessonId: lesson.id,
            title: lesson.title,
            stepIndex,
          });
        } catch {
          /* non-fatal */
        }
      }
    }
    navigate('/today');
  }, [uid, lesson, sequence.length, navigate]);

  const goNext = useCallback(() => {
    if (advanceLock.current || done) return;
    advanceLock.current = true;
    window.setTimeout(() => {
      advanceLock.current = false;
    }, 300);
    if (index >= sequence.length - 1) {
      finish();
      return;
    }
    setIndex((i) => i + 1);
  }, [index, sequence.length, done, finish]);

  const goPrev = useCallback(() => {
    setDone(false);
    setIndex((i) => Math.max(0, i - 1));
  }, []);

  const remaining = useHoldTimer({
    holdSeconds,
    paused: paused || done || !step,
    onComplete: goNext,
  });

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === ' ' || e.code === 'Space') {
        e.preventDefault();
        setPaused((p) => !p);
      } else if (e.key === 'ArrowRight') {
        e.preventDefault();
        goNext();
      } else if (e.key === 'ArrowLeft') {
        e.preventDefault();
        goPrev();
      } else if (e.key === 'Escape') {
        exitPractice();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [goNext, goPrev, exitPractice]);

  if (error) {
    return (
      <div className="cs-page yoga-shell">
        <div className="cs-banner b-danger" role="alert">
          Could not load class: {error}
        </div>
        <Link to="/library">Back to Library</Link>
      </div>
    );
  }

  if (!lesson) {
    return (
      <div className="cs-page yoga-shell">
        <p className="cs-help">
          <span className="cs-spinner" aria-hidden /> Loading practice…
        </p>
      </div>
    );
  }

  if (!sequence.length) {
    return (
      <div className="cs-page yoga-shell u-stack" style={{ '--gap': 'var(--sp-4)' }}>
        <h1 className="yoga-hero-brand" style={{ fontSize: '2rem' }}>{lesson.title}</h1>
        <p className="yoga-hero-lead">This class has no pose sequence yet.</p>
        <Link className="cs-btn primary" to="/library">Back to Library</Link>
      </div>
    );
  }

  if (done) {
    const practiced = practicedSeconds(sequence, sequence.length, 0);
    return (
      <div className="yoga-player yoga-player--done m-fade-in">
        <div className="yoga-player__end u-stack" style={{ '--gap': 'var(--sp-4)' }}>
          <p className="yoga-player__kicker">Practice complete</p>
          <h1 className="yoga-hero-brand" style={{ fontSize: 'clamp(2rem, 5vw, 2.8rem)' }}>
            YogaKai
          </h1>
          <p style={{ margin: 0, fontSize: '1.1rem' }}>{lesson.title}</p>
          <p className="u-muted" style={{ margin: 0 }}>
            You practiced {formatDuration(practiced)} of {formatDuration(plannedDurationSeconds(sequence))}.
          </p>
          {statsPatch ? (
            <p className="u-muted u-small" style={{ margin: 0 }}>
              Streak {statsPatch.streakDays} day{statsPatch.streakDays === 1 ? '' : 's'}
              {' · '}
              {statsPatch.totalSessions} session{statsPatch.totalSessions === 1 ? '' : 's'} total
              {' · '}
              Saved on Today
            </p>
          ) : null}
          <div className="u-cluster" style={{ '--gap': 'var(--sp-3)', justifyContent: 'center' }}>
            <Link className="cs-btn primary" to="/today">Today</Link>
            <Link className="cs-btn" to={`/practice/${lesson.id}`}>Practice again</Link>
            <Link className="cs-btn cs-btn-ghost" to="/library">Library</Link>
          </div>
        </div>
      </div>
    );
  }

  const dots = sequence.map((_, i) => (
    <span key={i} className={i <= index ? 'on' : undefined} />
  ));

  return (
    <div className="yoga-player m-fade-in">
      <header className="yoga-player__top">
        <button type="button" className="cs-btn cs-btn-ghost cs-btn-sm" onClick={exitPractice}>
          Exit
        </button>
        <div className="yoga-player__title-block">
          <div className="yoga-player__class">{lesson.title}</div>
          <div className="u-muted u-small">
            {index + 1} / {sequence.length}
          </div>
        </div>
        <span className="u-muted u-small" style={{ minWidth: '3.5rem', textAlign: 'right' }}>
          {paused ? 'Paused' : ' '}
        </span>
      </header>

      <div className="yoga-player__dots" aria-hidden>
        {dots}
      </div>

      <div className="yoga-player__stage">
        <PoseStill poseKey={step.poseKey} />
        <div className="yoga-player__info">
          <h1 className="yoga-player__pose-name">{stepPoseLabel(step)}</h1>
          <div className="yoga-player__clock" aria-live="polite">
            {formatDuration(Math.ceil(remaining))}
          </div>
          {step.cue ? <p className="yoga-player__cue">{step.cue}</p> : null}
          <BreathingGuide breathText={step.breath} paused={paused} />
          {showTips && (step.tip || step.mod) ? (
            <div className="yoga-player__teacher u-stack" style={{ '--gap': '6px' }}>
              {step.tip ? <p className="u-small" style={{ margin: 0 }}><strong>Tip</strong> {step.tip}</p> : null}
              {step.mod ? <p className="u-small" style={{ margin: 0 }}><strong>Mod</strong> {step.mod}</p> : null}
            </div>
          ) : null}
        </div>
      </div>

      <footer className="yoga-player__controls">
        <button
          type="button"
          className="yoga-player__round"
          onClick={goPrev}
          disabled={index === 0}
          aria-label="Previous pose"
        >
          Prev
        </button>
        <button
          type="button"
          className="yoga-player__round yoga-player__round--big"
          onClick={() => setPaused((p) => !p)}
          aria-pressed={paused}
          aria-label={paused ? 'Resume' : 'Pause'}
        >
          {paused ? 'Go' : 'Pause'}
        </button>
        <button
          type="button"
          className="yoga-player__round"
          onClick={goNext}
          aria-label={index >= sequence.length - 1 ? 'Finish' : 'Next pose'}
        >
          {index >= sequence.length - 1 ? 'Done' : 'Next'}
        </button>
      </footer>
    </div>
  );
}

export default function PracticePlayer() {
  return (
    <PlanGate>
      <PlayerInner />
    </PlanGate>
  );
}
